import Paper from "@material-ui/core/Paper";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import DateInput from "components/date-input";
import Input from "components/input";
import InputNumber from "components/input-number";
import { SelectBox } from "components/uikit-adapter";
import { GeneralSettingParameter } from "models/generalSetting/generalSetting";
import TipoUsuario from "models/usuarios/tipo-usuario";
import moment from "moment";
import PropTypes from "prop-types";
import intl from "react-intl-universal";
import { getGeneralSettingValue } from "services/general-settings/general-settings";
import { formatNumber } from "utils/format";
import { ISO_DATE_FORMAT } from "utils/format-date";
import Session from "utils/user-storage";
import { usesHourlyPlan } from "utils/validations-general-settings";
import { getValidationForInTransitStatus } from "./utils";

const StyledTableCell = withStyles(() => ({
  head: {
    borderBottom: "none",
    backgroundColor: "var(--color-action-default)",
    fontWeight: "var(--font-weight-semibold)",
    fontSize: 12,
    color: "white",
    borderRight: "1px solid var(--color-contrast-white)",
    lineHeight: "10px",
  },
  body: {
    fontSize: 14,
    color: "#313131",
    fontWeight: "var(--font-weight-regular)",
    borderRight: "1px solid #EDEDED",
    lineHeight: "14px",
    padding: "10px",
  },
  root: {
    fontFamily: "ProximaNova",
    padding: "8px",
    position: "relative ! important",
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
    padding: "8px",
  },
}))(TableRow);

const useStyles = makeStyles({
  root: {
    width: "100%",
    boxShadow: "none",
    height: "calc(100% - 290px)",
  },
  container: {
    overflowY: "auto",
    height: "-webkit-fill-available",
  },
  head: {
    fontWeight: "var(--font-weight-semibold)",
    color: "#A2A2A2",
  },
  bold: {
    fontWeight: "bold",
    textAlign: "center",
  },
  justificationColumn: {
    minWidth: 220,
    width: "20%",
  },
  supplierColumn: {
    minWidth: 220,
    width: "10%",
  },
  rowBold: {
    "& td": { fontWeight: "var(--font-weight-semibold) !important" },
  },
  inputBold: {
    "& *": { fontWeight: "var(--font-weight-semibold) !important" },
  },
});

export const OrdersChangeDateTable = ({
  data,
  justificationOptions = [],
  handleNewQuantity,
  handleNewCollectDateToARow,
  handleNewDeliveryDateToARow,
  handleJustificativaChangeToARow,
  handleNewOrigin,
  handleNewDeliveryTimeToARow,
}) => {
  const classes = useStyles();
  const isUsePoAutomations = getGeneralSettingValue(GeneralSettingParameter.UsePoAutomations);
  const user = Session.get();

  const getJustificationValue = (id) => {
    if (!id) return null;
    const opt = Array.isArray(justificationOptions)
      ? justificationOptions.find((o) => o.Id === id)
      : null;
    return opt ? { Id: id, Description: opt.Description } : null;
  };

  const isRowEdited = (row) => {
    const hasQty =
      row.newOrderQuantity !== undefined &&
      row.newOrderQuantity !== null &&
      row.newOrderQuantity !== "";
    const hasDeliveryTime =
      row.newDeliveryTime !== undefined &&
      row.newDeliveryTime !== null &&
      row.newDeliveryTime !== "";
    return Boolean(
      row.newCollectDate || row.newDeliveryDate || row.newOrigin || hasQty || hasDeliveryTime
    );
  };

  const showNewOriginCol = isUsePoAutomations && user.TipoUsuario === TipoUsuario.Fornecedor;

  return (
    <Paper className={classes.root}>
      <TableContainer className={classes.container}>
        <Table stickyHeader>
          <TableHead className={classes.head}>
            <TableRow>
              <StyledTableCell>{intl.get("commons.po")}</StyledTableCell>
              <StyledTableCell>{intl.get("commons.source")}</StyledTableCell>
              <StyledTableCell>{intl.get("commons.destiny")}</StyledTableCell>
              <StyledTableCell>{intl.get("commons.material")}</StyledTableCell>
              <StyledTableCell>{intl.get("commons.collectDate")}</StyledTableCell>
              <StyledTableCell>{intl.get("commons.deliveryDate")}</StyledTableCell>
              {usesHourlyPlan() && (
                <StyledTableCell>{intl.get("commons.deliveryTime")}</StyledTableCell>
              )}
              <StyledTableCell>{intl.get("commons.quantity")}</StyledTableCell>
              <StyledTableCell>
                {intl.get("stocks.informAdvanceOrder.newCollectionDate")}
              </StyledTableCell>
              <StyledTableCell>
                {intl.get("stocks.informAdvanceOrder.newDeliveryDate")}
              </StyledTableCell>
              {usesHourlyPlan() && (
                <StyledTableCell>
                  {intl.get("stocks.informAdvanceOrder.newDeliveryTime")}
                </StyledTableCell>
              )}
              {showNewOriginCol ? (
                <StyledTableCell>{intl.get("orders.newOrigin")}</StyledTableCell>
              ) : (
                <StyledTableCell>{intl.get("orders.newQuantity")}</StyledTableCell>
              )}
              <StyledTableCell className={classes.justificationColumn}>
                {intl.get("commons.justification")}
              </StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((row, index) => {
              const edited = isRowEdited(row); 
              const rowClassName = edited ? classes.rowBold : undefined;
              const inputClassName = edited ? classes.inputBold : undefined;           
              return (
                <StyledTableRow key={row.Id} className={rowClassName}>
                  <StyledTableCell>{row.SapCode ?? "-"}</StyledTableCell>
                  <StyledTableCell>{row.SupplierName}</StyledTableCell>
                  <StyledTableCell>{`${row.BusinessUnitPlant} - ${row.BusinessUnitName}`}</StyledTableCell>
                  <StyledTableCell>{row.MaterialName}</StyledTableCell>
                  <StyledTableCell>{moment(row.OrderCollectDate).format("L")}</StyledTableCell>
                  <StyledTableCell>{moment(row.OrderDeliveryDate).format("L")}</StyledTableCell>
                  {usesHourlyPlan() && (
                    <StyledTableCell className={rowClassName}>
                      {row.OrderDeliveryTime
                        ? moment(row.OrderDeliveryTime, "HH:mm:ss").format("HH:mm")
                        : "-"}
                    </StyledTableCell>
                  )}
                  <StyledTableCell>
                    {`${formatNumber(row.OrderQuantity)} ${row.MaterialUnitOfMeasure}`}
                  </StyledTableCell>
                  <StyledTableCell className={classes.bold}>
                    <DateInput
                      value={row.newCollectDate}
                      onChange={(value) => handleNewCollectDateToARow(row, value)}
                      canClear
                      minDate={moment().format(ISO_DATE_FORMAT)}
                      data-testid="new-collection-date"
                      disabled={getValidationForInTransitStatus(row.OrderStatus)}
                      className={inputClassName}
                    />
                  </StyledTableCell>

                  <StyledTableCell className={classes.bold}>
                    <DateInput
                      value={row.newDeliveryDate}
                      onChange={(value) => handleNewDeliveryDateToARow(row, value)}
                      canClear
                      minDate={moment().format(ISO_DATE_FORMAT)}
                      data-testid="new-delivery-date"
                      className={inputClassName}
                    />
                  </StyledTableCell>
                  {usesHourlyPlan() && (
                    <StyledTableCell className={classes.bold}>
                      <Input
                        name={`newDeliveryTime-${row.Id}`}
                        htmlType="time"
                        value={row.newDeliveryTime || ""}
                        onChange={(event) => handleNewDeliveryTimeToARow(row, event.target.value)}
                        data-testid="new-delivery-time"
                        className={inputClassName}
                        disabled={!row.OrderDeliveryTime}
                      />
                    </StyledTableCell>
                  )}
                  {showNewOriginCol ? (
                    <StyledTableCell className={classes.supplierColumn}>
                      {row?.SupplierList?.length || row.OrderStatus !== 7 ? (
                        <SelectBox
                          name={`newOrigin-${row.Id}`}
                          placeholder={intl.get("commons.selectJustification")}
                          valueKey="Id"
                          labelKey="ShortDescription"
                          value={row.newOrigin ?? row.SupplierId}
                          options={row.OrderStatus === 7 ? row.SupplierList : []}
                          disabled={row.OrderStatus !== 7}
                          onChange={(selected) => {
                            handleNewOrigin(row, selected || null);
                          }}
                          searchable
                          dontShowLabel
                          clearable
                          className={inputClassName}
                        />
                      ) : (
                        <p>Não há opções</p>
                      )}
                    </StyledTableCell>
                  ) : (
                    <StyledTableCell>
                      <InputNumber
                        value={row.newOrderQuantity}
                        onChange={(value) => handleNewQuantity(value, index)}
                        className={`input-new-quantity ${inputClassName || ""}`}
                        type="default"
                        allowDecimals
                        decimalsLimit={3}
                        data-testid="input-new-quantity"
                        disabled={getValidationForInTransitStatus(row.OrderStatus)}
                      />
                    </StyledTableCell>
                  )}
                  <StyledTableCell className={classes.justificationColumn}>
                    <SelectBox
                      name={`justificativa-${row.Id}`}
                      placeholder={intl.get("commons.selectJustification")}
                      valueKey="Id"
                      labelKey="Description"
                      value={getJustificationValue(row.justificativa)}
                      options={justificationOptions}
                      onChange={(selected) =>
                        handleJustificativaChangeToARow(row, selected ? selected.Id : "")
                      }
                      searchable
                      dontShowLabel
                      className={inputClassName}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

OrdersChangeDateTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      Id: PropTypes.any,
      OrderStatus: PropTypes.any,
      SupplierName: PropTypes.string,
      BusinessUnitPlant: PropTypes.string,
      BusinessUnitName: PropTypes.string,
      MaterialName: PropTypes.string,
      OrderCollectDate: PropTypes.string,
      OrderDeliveryDate: PropTypes.string,
      OrderQuantity: PropTypes.any,
      OrderDeliveryTime: PropTypes.any,
      MaterialUnitOfMeasure: PropTypes.string,
      newCollectDate: PropTypes.any,
      newDeliveryDate: PropTypes.any,
      newOrderQuantity: PropTypes.any,
      newDeliveryTime: PropTypes.any,
    })
  ).isRequired,
  handleNewQuantity: PropTypes.func.isRequired,
  handleNewCollectDateToARow: PropTypes.func.isRequired,
  handleJustificativaChangeToARow: PropTypes.func.isRequired,
  handleNewOrigin: PropTypes.func.isRequired,
  handleNewDeliveryDateToARow: PropTypes.func.isRequired,
  handleNewDeliveryTimeToARow: PropTypes.func.isRequired,
  justificationOptions: PropTypes.arrayOf(
    PropTypes.shape({
      Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      Description: PropTypes.string.isRequired,
    })
  ).isRequired,
};